// React
import React from 'react';
import PropTypes from 'prop-types';
import { ChromePicker } from 'react-color';
// Locales
import translate from 'locales/i18n';
// Material UI
import { withStyles } from '@mui/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { TransitionGroup } from 'react-transition-group';
import { Collapse } from '@mui/material';
// Actions
import SiteAction from 'actions/SiteAction';
// PCP Components
import DialogSPEConfiguration from 'components/dialogs/site/DialogSPEConfiguration';
import DialogSliderConfiguration from 'components/dialogs/site/DialogSliderConfiguration';
import DialogAdboxConfiguration from 'components/dialogs/site/DialogAdboxConfiguration';
// Facade
import Facade from 'core/Facade';
// Countries
import { countries, countryToFlag } from 'components/dialogs/proxy/DialogEditProxyUtils';

class DialogManageSite extends React.PureComponent {

    getInitialState = () => ({
        name: '',
        url: '',
        lat: 0,
        long: 0,
        color: '#000000',
        speConfig: undefined,
        sliderConfig: undefined,
        adboxConfig: undefined,
        proxyType: 'TOR',
        proxyCountry: 'NONE',
        comment: '',
        errorMsg: '',
        id: null,
        dialogSPEConfigurationOpen: false,
        dialogSliderConfigurationOpen: false,
        dialogAdboxConfigurationOpen: false,
        alternativeUrls: [],
        imgUrl: '',
        dialogColorPicker: false,
    });

    state = this.getInitialState();

    static getDerivedStateFromProps(props, state) {
        if (props.site && props.site.id !== state.id) {
            return {
                name: props.site.name,
                url: props.site.url,
                speConfig: props.site.speConfig ? props.site.speConfig : state.speConfig,
                sliderConfig: props.site.sliderConfig ? props.site.sliderConfig : state.sliderConfig,
                adboxConfig: props.site.adboxConfig ? props.site.adboxConfig : state.adboxConfig,
                lat: props.site.lat,
                long: props.site.long,
                color: props.site.color,
                proxyType: props.site.proxyType,
                proxyCountry: props.site.proxyCountry,
                comment: props.site.comment,
                id: props.site.id,
                alternativeUrls: props.site.alternativeUrls,
                imgUrl: props.site.imgUrl ? props.site.imgUrl:''
            };
        } return null;
    }

    static propTypes = {
        close: PropTypes.func,
        open: PropTypes.bool,
        user: PropTypes.object,
        classes: PropTypes.object,
        fullScreen: PropTypes.bool,
        site: PropTypes.object
    };

    checkInfo = type => {
        const { name, url, color } = this.state;

        if (!name && type === 'add') {
            this.setState({ errorMsg: translate.i18n('ERROR_MSG', { name: translate.i18n('NAME') }) });
            return false;
        }

        if (!url && type === 'add') {
            this.setState({ errorMsg: translate.i18n('ERROR_MSG', { name: translate.i18n('URL') }) });
            return false;
        }

        if (!color && type === 'add') {
            this.setState({ errorMsg: translate.i18n('ERROR_MSG', { name: translate.i18n('COLOR') }) });
            return false;
        }

        return true;
    };

    getDefaultSpeConfig = () => ({
        isMarketplace: false,
        trustAlertThreshold: 0.001,
        robot: 'V3',
        currency: 'EUR',
        tax: 'INCL',
        bwRef: false,
        bwConfig: false,
        comment: '',
        maxParallelWorkers: 2,
        scrapingControlDuration: 15000
    });

    getDefaultSliderConfig = () => ({
        altTag: '',
        imgListTag: '',
        infoTag: '',
        nameTag: '',
        query: '',
        titleTag: '',
        urlTag: '',
        url: '',
        robot: ''
    });

    getDefaultAdboxConfig = () => ({
        senderMails: []
    });

    addSite = () => {
        const { name, url, color, lat, speConfig, long, proxyType, proxyCountry, comment, sliderConfig, alternativeUrls, imgUrl, adboxConfig } = this.state;

        const urls = alternativeUrls.filter((alternativeUrl) => alternativeUrl !== '');

        const newImgUrl = imgUrl? imgUrl:`https://www.google.com/s2/favicons?domain=${url}`;

        if (this.checkInfo('add')) {
            SiteAction.addSite({
                name,
                url,
                color,
                comment,
                lat: parseFloat(lat),
                long: parseFloat(long),
                proxyType,
                proxyCountry: proxyCountry !== 'NONE' ? proxyCountry : undefined,
                speConfig,
                sliderConfig,
                alternativeUrls: urls,
                imgUrl: newImgUrl,
                adboxConfig
            });
            this.close();
        }
    };

    updateSite = () => {
        const { name, url, color, lat, long, proxyType, proxyCountry, speConfig, comment, sliderConfig, alternativeUrls, imgUrl, adboxConfig } = this.state;

        const urls = alternativeUrls.filter((alternativeUrl) => alternativeUrl !== '');

        if (this.checkInfo('update')) {
            SiteAction.updateSite({
                name,
                url,
                color,
                comment,
                lat: parseFloat(lat),
                long: parseFloat(long),
                proxyType,
                proxyCountry: proxyCountry !== 'NONE' ? proxyCountry : undefined,
                speConfig,
                sliderConfig,
                alternativeUrls: urls,
                imgUrl,
                adboxConfig
            }, this.props.site.id);
            this.close();
        }
    };

    close = () => {
        this.setState(this.getInitialState());
        this.props.close();
    };

    handleChange = (name) => event => {
        this.setState({
            [name]: event.target.value,
            errorMsg: ''
        });
    };

    addAltUrl() {
        this.setState(state => {
            const alternativeUrls = [...state.alternativeUrls, ''];
            return { alternativeUrls };
        });
    }

    deleteUrl(index) {
        this.setState(state => {
            const alternativeUrls = state.alternativeUrls.filter((url, i) => i !== index);
            return { alternativeUrls };
        });
    }

    updateAltUrl = (index) => event => {
        const { value } = event.target;
        this.setState(state => {
            const alternativeUrls = state.alternativeUrls.map((url, i) => {
                if (i === index) {
                    return value;
                }
                return url;
            });
            return { alternativeUrls };
        });
    };

    handleChangeSiteConfiguration = (attr, value) => {
        const newConfig = this.state.speConfig;

        newConfig[attr] = value;

        this.setState({
            speConfig: Object.assign({}, newConfig)
        });
    };

    handleChangeSliderConfiguration = (attr, value) => {
        const newConfig = this.state.sliderConfig;

        newConfig[attr] = value;

        this.setState({
            sliderConfig: Object.assign({}, newConfig)
        });
    };

    handleChangeAdboxConfiguration = (attr, value) => {
        const newConfig = this.state.adboxConfig;

        newConfig[attr] = value;

        this.setState({ adboxConfig: Object.assign({}, newConfig) });
    };

    renderDialogSliderConfiguration = () => {
        const sliderConfig = this.state.sliderConfig ? this.state.sliderConfig : this.getDefaultSliderConfig();
        const { altTag, imgListTag, infoTag, nameTag, query, titleTag, urlTag, url, robot } = sliderConfig;

        return (
            <DialogSliderConfiguration
                close={() => this.setState({ dialogSliderConfigurationOpen: false })}
                open={this.state.dialogSliderConfigurationOpen}
                altTag={altTag ? altTag : ''}
                imgListTag={imgListTag ? imgListTag : ''}
                infoTag={infoTag ? infoTag : ''}
                nameTag={nameTag ? nameTag : ''}
                query={query ? query : ''}
                titleTag={titleTag ? titleTag : ''}
                urlTag={urlTag ? urlTag : ''}
                url={url ? url : ''}
                handleChangeSliderConfiguration={this.handleChangeSliderConfiguration}
                restoreDefaultSettings={() => this.setState({ sliderConfig: this.getDefaultSliderConfig() })}
                robot={robot ? robot : ''}
                siteName={this.state.name}
            />
        );
    };

    renderDialogSPEConfiguration = () => {
        const speConfig = this.state.speConfig ? this.state.speConfig : this.getDefaultSpeConfig();
        const { isMarketplace, robot, trustAlertThreshold, currency, tax, bwRef, bwConfig, comment, maxParallelWorkers, scrapingControlDuration } = speConfig;

        return (
            <DialogSPEConfiguration
                close={() => this.setState({ dialogSPEConfigurationOpen: false })}
                open={this.state.dialogSPEConfigurationOpen}
                isMarketplace={isMarketplace ? isMarketplace : false}
                robot={robot ? robot : 'V3'}
                trustAlertThreshold={trustAlertThreshold === undefined ? 0.01 : trustAlertThreshold}
                currency={currency ? currency : 'EUR'}
                tax={tax ? tax : 'INCL'}
                bwRef={bwRef ? bwRef : false}
                bwConfig={bwConfig ? bwConfig : false}
                comment={comment ? comment : ''}
                maxParallelWorkers={maxParallelWorkers === undefined ? 2 : maxParallelWorkers}
                scrapingControlDuration={scrapingControlDuration === undefined ? 15000 : scrapingControlDuration}
                handleChangeSiteConfiguration={this.handleChangeSiteConfiguration}
                restoreDefaultSettings={() => this.setState({ speConfig: this.getDefaultSpeConfig() })}
                siteName={this.state.name}
            />
        );
    };

    renderDialogAdboxConfiguration = () => <DialogAdboxConfiguration
        open={this.state.dialogAdboxConfigurationOpen}
        close={() => this.setState({ dialogAdboxConfigurationOpen: false })}
        senderMails={this.state.adboxConfig ? this.state.adboxConfig.senderMails : undefined}
        setSendersMails={this.handleChangeAdboxConfiguration}
    />;

    renderDialogColorPicker = () => <Dialog open={this.state.dialogColorPicker} onClose={() => this.setState({ dialogColorPicker: false })}>
        <ChromePicker
            onChangeComplete={newColor => this.setState({ color: newColor.hex })}
            color={this.state.color}
            label={translate.i18n('COLOR')} />
    </Dialog>;

    render() {
        const { fullScreen, classes, site } = this.props;
        const { name, url, lat, long, color, errorMsg, comment, speConfig, sliderConfig, proxyType, proxyCountry, alternativeUrls, imgUrl, adboxConfig } = this.state;

        return (
            <Dialog
                fullScreen={fullScreen}
                open={this.props.open}
                onClose={this.close}
                disableRestoreFocus={true}
                aria-labelledby='responsive-dialog-title'
                classes={{
                    paper: classes.dialogPaper
                }}
            >
                <IconButton color='secondary' onClick={this.close} className={classes.closeButton}>
                    <ClearIcon />
                </IconButton>
                <DialogTitle id='responsive-dialog-title' className={classes.dialogTitle}>{site ? translate.i18n('EDIT_SITE') : translate.i18n('ADD_SITE')}</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <TextField
                        disabled={!!site}
                        required
                        label={translate.i18n('NAME')}
                        value={name}
                        variant='outlined'
                        className={classes.textField}
                        onChange={this.handleChange('name')}
                    />
                    <div className={classes.urlContainer}>
                        <TextField
                            disabled={!!site}
                            required
                            label={translate.i18n('URL')}
                            value={url}
                            variant='outlined'
                            className={classes.textField}
                            onChange={this.handleChange('url')}
                        />
                        <IconButton edge='end' aria-label='URL alternatives' style={{ marginTop: '16px' }} onClick={() => this.addAltUrl()}>
                            <AddIcon />
                        </IconButton>
                    </div>
                    <TransitionGroup className={classes.collapseWrapper}>
                        {alternativeUrls.map((alternativeUrl, index) =>
                            <Collapse key={index} >
                                <div className={classes.urlContainer}>
                                    <TextField
                                        label={translate.i18n('ALTERNATIVE_URL') + (index + 1)}
                                        value={alternativeUrl}
                                        variant='outlined'
                                        className={classes.textField}
                                        onChange={this.updateAltUrl(index)}
                                    />
                                    <IconButton edge='end' aria-label='URL alternatives' style={{ marginTop: '16px' }} onClick={() => this.deleteUrl(index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            </Collapse>
                        )}
                    </TransitionGroup>
                    <TextField
                        type='number'
                        label={translate.i18n('LAT')}
                        value={lat}
                        variant='outlined'
                        className={classes.textField}
                        onChange={this.handleChange('lat')}
                    />
                    <TextField
                        type='number'
                        label={translate.i18n('LONG')}
                        value={long}
                        variant='outlined'
                        className={classes.textField}
                        onChange={this.handleChange('long')}
                    />
                    <div className={classes.textField}>
                        <TextField
                            label='Couleur'
                            value={color}
                            onChange={this.handleChange('color')}
                            InputProps={{
                                endAdornment: <div style={{ height: '15px', width: '15px', backgroundColor: color }}> </div>,
                            }}
                        />
                        <Button onClick={() => this.setState({ dialogColorPicker: true })} style={{ marginTop: '10px' }}>
                            Couleurs
                        </Button>
                        {this.renderDialogColorPicker()}
                    </div>
                    <TextField
                        label='Image url'
                        value={imgUrl}
                        variant='outlined'
                        className={classes.textField}
                        onChange={this.handleChange('imgUrl')}
                    />
                    <FormControl variant='outlined'
                        classes={{
                            root: classes.textField
                        }}>
                        <InputLabel
                            htmlFor='outlined'
                            margin={'dense'}>
                            {translate.i18n('PROXY_TYPE')}
                        </InputLabel>
                        <Select
                            value={proxyType}
                            onChange={this.handleChange('proxyType')}
                            label={translate.i18n('PROXY_TYPE')}
                        >
                            <MenuItem value={'TOR'}>{'TOR'}</MenuItem>
                            <MenuItem value={'TRANSPARENT'}>{'TRANSPARENT'}</MenuItem>
                            <MenuItem value={'DATACENTER'}>{'DATACENTER'}</MenuItem>
                            <MenuItem value={'RESIDENTIAL'}>{'RESIDENTIAL'}</MenuItem>
                            <MenuItem value={'MOBILE'}>{'MOBILE'}</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl variant='outlined'
                        classes={{
                            root: classes.textField
                        }}>
                        <InputLabel
                            htmlFor='outlined'
                            margin={'dense'}>
                            {translate.i18n('PROXY_COUNTRY')}
                        </InputLabel>
                        <Select
                            value={proxyCountry}
                            onChange={this.handleChange('proxyCountry')}
                            label={translate.i18n('PROXY_COUNTRY')}
                        >
                            <MenuItem value={'NONE'}>{translate.i18n('NONE')}</MenuItem>
                            {countries.map(country => <MenuItem key={country.code} value={country.code}><span style={{ marginRight: '2px' }}>{countryToFlag(country.code)}</span>{country.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField
                        className={classes.textField}
                        id='outlined-multiline-static'
                        label={translate.i18n('COMMENT')}
                        multiline
                        rows={2}
                        value={comment}
                        variant='outlined'
                        onChange={this.handleChange('comment')}
                    />
                    <div className={classes.configurationButtonContainer}>
                        <p className={classes.configLabel} >{translate.i18n('CONFIGURATION_SPE') + ' :'}</p>
                        <IconButton
                            style={{ marginLeft: 'auto' }}
                            disabled={Facade.isEmpty(speConfig)}
                            variant='outlined'
                            color='primary'
                            onClick={() => this.setState({ dialogSPEConfigurationOpen: true })}>
                            <EditIcon />
                        </IconButton>
                        <IconButton
                            disabled={!Facade.isEmpty(speConfig)}
                            variant='outlined'
                            color='primary'
                            onClick={() => this.setState({ dialogSPEConfigurationOpen: true, speConfig: this.props.site && this.props.site.speConfig ? this.props.site.speConfig : this.getDefaultSpeConfig() })}>
                            <AddIcon />
                        </IconButton>
                        <IconButton
                            disabled={Facade.isEmpty(speConfig)}
                            variant='outlined'
                            color='secondary'
                            onClick={() => this.setState({ speConfig: {} })}>
                            <DeleteIcon />
                        </IconButton>
                    </div>
                    <div className={classes.configurationButtonContainer}>
                        <p className={classes.configLabel} >{translate.i18n('CONFIGURATION_SLIDER') + ' :'}</p>
                        <IconButton
                            style={{ marginLeft: 'auto' }}
                            disabled={Facade.isEmpty(sliderConfig)}
                            variant='outlined'
                            color='primary'
                            onClick={() => this.setState({ dialogSliderConfigurationOpen: true })}>
                            <EditIcon />
                        </IconButton>
                        <IconButton
                            disabled={!Facade.isEmpty(sliderConfig)}
                            variant='outlined'
                            color='primary'
                            onClick={() => this.setState({ dialogSliderConfigurationOpen: true, sliderConfig: this.props.site && this.props.site.sliderConfig ? this.props.site.sliderConfig : this.getDefaultSliderConfig() })}>
                            <AddIcon />
                        </IconButton>
                        <IconButton
                            disabled={Facade.isEmpty(sliderConfig)}
                            variant='outlined'
                            color='secondary'
                            onClick={() => this.setState({ sliderConfig: {} })}>
                            <DeleteIcon />
                        </IconButton>
                    </div>
                    <div className={classes.configurationButtonContainer}>
                        <p className={classes.configLabel} >AdBox Configuration :</p>
                        <IconButton
                            style={{ marginLeft: 'auto' }}
                            disabled={Facade.isEmpty(adboxConfig)}
                            variant='outlined'
                            color='primary'
                            onClick={() => this.setState({ dialogAdboxConfigurationOpen: true })}>
                            <EditIcon />
                        </IconButton>
                        <IconButton
                            disabled={!Facade.isEmpty(adboxConfig)}
                            variant='outlined'
                            color='primary'
                            onClick={() => this.setState({ dialogAdboxConfigurationOpen: true, adboxConfig: this.props.site && this.props.site.adboxConfig ? this.props.site.adboxConfig : this.getDefaultAdboxConfig() })}>
                            <AddIcon />
                        </IconButton>
                        <IconButton
                            disabled={Facade.isEmpty(adboxConfig)}
                            variant='outlined'
                            color='secondary'
                            onClick={() => this.setState({ adboxConfig: {} })}>
                            <DeleteIcon />
                        </IconButton>
                    </div>
                    {errorMsg ?
                        <p className={classes.errorMsg}>{errorMsg}</p>
                        :
                        null
                    }
                </DialogContent>
                <DialogActions>
                    <Button color='secondary'
                        onClick={this.close}>
                        <ClearIcon className={classes.leftIcon} />
                        <div>{translate.i18n('CANCEL')}</div>
                    </Button>
                    <Button color='primary'
                        onClick={site ? this.updateSite : this.addSite}>
                        <AddIcon className={classes.leftIcon} />
                        <div>{site ? translate.i18n('SAVE') : translate.i18n('ADD_SITE')}</div>
                    </Button>
                </DialogActions>
                {this.renderDialogSPEConfiguration()}
                {this.renderDialogSliderConfiguration()}
                {this.renderDialogAdboxConfiguration()}
            </Dialog>
        );
    }
}

const styles = theme => ({
    dialogContent: {
        overflow: 'auto',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 0,
        minWidth: '400px'
    },
    dialogPaper: {
        minWidth: '25%'
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingBottom: '5px'
    },
    textField: {
        marginTop: '16px',
        width: '80%'
    },
    closeButton: {
        position: 'absolute',
        right: '2px',
        top: '2px'
    },
    errorMsg: {
        fontSize: '12px',
        marginBottom: '0',
        marginTop: '8px',
        fontFamily: 'roboto',
        fontStyle: 'italic',
        color: theme.palette.red.main
    },
    configurationButtonContainer: {
        border: 'solid 1px ' + theme.palette.grey.border,
        borderRadius: '4px',
        paddingLeft: '8px',
        alignItems: 'center',
        display: 'flex',
        marginTop: '12px',
        marginBottom: '4px',
        width: '80%'
    },
    configLabel: {
        margin: 0,
        fontSize: '14px'
    },
    leftIcon: {
        marginRight: '8px',
        fontSize: 20
    },
    collapseWrapper: {
        width: '100%'
    },
    urlContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        width: '100%',
        paddingLeft: '10%',
        alignItems: 'center'
    }
});

export default withStyles(styles)(DialogManageSite);
